import { useCallback, useEffect, useState } from 'react';
import IntegrationCard from '../Shared/IntegrationCard';
import { AppBar, Box, CircularProgress, Divider, Grid2, Typography } from '@mui/material';
import POSSwitch from '@/components/POS/Common/POSSwitch';
import POSSelect from '@/components/POS/Common/POSSelect';
import POSAutocomplete from '@/components/POS/Common/POSAutocomplete';
import POSButton from '@/components/POS/Common/POSButton';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { api } from '@/utils/Api/POS';
import { toast } from 'react-toastify';
import { accountOption, CoaConfig } from '../../Types/Integration-type';

const defaultCoa = {
    sales: {
        withVat: 1000,
        withoutVat: 1050,
    },
    paymentMethods: {
        cash: 55000,
        card: 55000,
        mobilePay: 55000,
        bankTransfer: 55000,
        invoice: 55000,
        onlinePayment: 55000,
        giftCard: 63080,
        rockCard: 63080,
        prepayment: 63080,
        post: 63100,
        outstanding: 63100,
    },
    general: {
        toBank: 55000,
        cashDifference: 3000,
    },
    receipts: {
        individualSalesReceipts: true,
    },
};

const paymentMethodFields: Array<{
    key: keyof CoaConfig['paymentMethods'];
    label: string;
}> = [
    { key: 'cash', label: 'POS.Cash' },
    { key: 'bankTransfer', label: 'POS.BankTransfer' },
    { key: 'mobilePay', label: 'Integration.MobilePay' },
    { key: 'card', label: 'POS.Card' },
    { key: 'outstanding', label: 'POS.Outstanding' },
    { key: 'giftCard', label: 'POS.GiftCard' },
    { key: 'rockCard', label: 'PunchCard.PunchCard' },
    { key: 'invoice', label: 'Integration.Invoice' },
    { key: 'onlinePayment', label: 'Integration.OnlinePayment' },
    { key: 'prepayment', label: 'Integration.Prepayment' },
    { key: 'post', label: 'Integration.Post' },
];

const DineroBox = () => {
    const [companyList, setCompanyList] = useState<any[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [accountList, setAccountList] = useState<accountOption[]>([]);

    const [initialValues, setInitialValues] = useState<CoaConfig>(defaultCoa);
    const [isOrganizationLoading, setIsOrganizationLoading] = useState(false);
    const [isAccountLoading, setIsAccountLoading] = useState(false);

    const formik = useFormik<CoaConfig>({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const saveData = {
                organizationId: selectedCompany,
                coaConfiguration: {
                    ...values,
                    sales: {
                        withVat: values.sales.withVat ?? defaultCoa.sales.withVat,
                        withoutVat: values.sales.withoutVat ?? defaultCoa.sales.withoutVat,
                    },
                },
            };

            try {
                const response = await api.putApiDineroConfig({ ...saveData });
                await fetchCompanyMenuList(selectedCompany);
                toast.success(response?.message);
            } catch (err) {
                console.log('Error saving data:', err);
            }
        },
    });

    const fetchCompanyMenuList = useCallback(async (companyId: string) => {
        try {
            setIsAccountLoading(true);
            const accounts = await api.getApiDineroOrganizationsOrganizationIdAccounts(companyId);

            if (!accounts.success) {
                throw new Error('Failed to fetch accounts');
            }

            if (accounts?.data) {
                const options = accounts.data.map((account) => ({
                    label: `${account.AccountNumber}-${account.Name}`,
                    value: account.AccountNumber,
                }));
                setAccountList(options);
            }

            const incomingConfig = accounts.coaConfiguration as Partial<CoaConfig> | undefined;
            const coaData: CoaConfig = {
                sales: {
                    ...defaultCoa.sales,
                    ...(incomingConfig?.sales || {}),
                },
                paymentMethods: {
                    ...defaultCoa.paymentMethods,
                    ...(incomingConfig?.paymentMethods || {}),
                },
                general: {
                    ...defaultCoa.general,
                    ...(incomingConfig?.general || {}),
                },
                receipts: {
                    ...defaultCoa.receipts,
                    ...(incomingConfig?.receipts || {}),
                },
            };
            setInitialValues(coaData);
        } catch (err) {
            setAccountList([]);
            setInitialValues(defaultCoa);
            toast.error(t('Integration.FailedToFetchDineroAccounts'));
        } finally {
            setIsAccountLoading(false);
        }
    }, []);

    const fetchCompaniesAndAccounts = useCallback(
        async (cid: string) => {
            try {
                setIsOrganizationLoading(true);
                const organizationList = await api.getApiDineroOrganizations();
                const organizations = organizationList?.data || [];
                setCompanyList(organizations);
                if (organizations.length > 0) {
                    const selected = organizations.find((company) => company.Id === cid) || organizations[0];
                    setSelectedCompany(selected.Id);
                    await fetchCompanyMenuList(selected.Id);
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
            } finally {
                setIsOrganizationLoading(false);
            }
        },
        [fetchCompanyMenuList],
    );

    useEffect(() => {
        fetchCompaniesAndAccounts('');
    }, [fetchCompaniesAndAccounts]);

    const handleAccountChange = (section: 'sales' | 'paymentMethods' | 'general', field: string, value: any) => {
        formik.setFieldValue(`${section}.${field}`, value ? Number(value) : value);
    };

    const renderAccountAutocomplete = (section: 'sales' | 'paymentMethods' | 'general', field: string) => {
        const options = accountList.map((account) => ({
            id: account.value,
            name: account.label,
        }));
        const selectedValue = formik.values[section][field as keyof (typeof formik.values)[typeof section]];
        const selectedOption = options.find((option) => option.id === selectedValue) || null;

        return (
            <POSAutocomplete
                options={options}
                value={selectedOption}
                onChange={(_, value) => handleAccountChange(section, field, value?.id ?? null)}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    return (
        <>
            {formik.dirty && (
                <AppBar
                    sx={{
                        position: 'fixed',
                        top: 45,
                        left: 0,
                        right: 0,
                        py: 1,
                        px: 4,
                        bgcolor: '#fff',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        width: '100%',
                        zIndex: 8,
                        mb: 5,
                    }}
                >
                    <POSButton
                        variant="save"
                        width="auto"
                        height={35}
                        title={t('Setting.SaveChanges')}
                        onClick={() => formik.handleSubmit()}
                    />
                </AppBar>
            )}
            {/* Company Selection */}
            <Box sx={{ mt: formik.dirty ? 8 : 0 }}>
                <IntegrationCard
                    title={t('Integration.Dinero')}
                    processContent={
                        <Box>
                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 'bold' }}>{t('POS.Company')}</Typography>
                                {(isOrganizationLoading || isAccountLoading) && <CircularProgress size={16} />}
                            </Box>
                            <POSSelect
                                options={companyList.map((company) => ({
                                    label: company.Name,
                                    value: company.Id,
                                }))}
                                value={selectedCompany}
                                onChange={async (e) => {
                                    const newCompanyId = e.target.value as string;
                                    setSelectedCompany(newCompanyId);
                                    await fetchCompanyMenuList(newCompanyId);
                                }}
                            />
                        </Box>
                    }
                />

                <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                {/* Sales Mapping */}
                <IntegrationCard
                    title={`${t('Integration.MappingSale')} ${t('Integration.Dinero')}`}
                    description={t('Integration.MappingSaleDescription')}
                    processContent={
                        <Grid2 container spacing={3}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                    {t('Integration.SaleWithVAT')}
                                </Typography>
                                {renderAccountAutocomplete('sales', 'withVat')}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                    {t('Integration.SalesWithoutVAT')}
                                </Typography>
                                {renderAccountAutocomplete('sales', 'withoutVat')}
                            </Grid2>
                        </Grid2>
                    }
                />

                <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                {/* Payment Mapping */}
                <IntegrationCard
                    title={`${t('Integration.MappingPayment')}`}
                    description={t('Integration.MappingPaymentDescription')}
                    processContent={
                        <Grid2 container spacing={3}>
                            {paymentMethodFields.map((field) => (
                                <Grid2 key={field.key} size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ mb: 1, fontWeight: 'bold' }}>{t(field.label)}</Typography>
                                    {renderAccountAutocomplete('paymentMethods', field.key)}
                                </Grid2>
                            ))}
                        </Grid2>
                    }
                />

                <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                <IntegrationCard
                    title={t('Integration.GeneralSettings')}
                    description={t('Integration.GeneralSettingsDescription')}
                    processContent={
                        <Grid2 container spacing={3}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>{t('Integration.ToBank')}</Typography>
                                {renderAccountAutocomplete('general', 'toBank')}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                    {t('Integration.CashDifference')}
                                </Typography>
                                {renderAccountAutocomplete('general', 'cashDifference')}
                            </Grid2>
                        </Grid2>
                    }
                />

                <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                <IntegrationCard
                    title={t('Integration.Receipts')}
                    description={t('Integration.ReceiptsDescription')}
                    processContent={
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                {t('Integration.IndividualSalesReceipts')}
                            </Typography>
                            <POSSwitch
                                checked={formik.values.receipts.individualSalesReceipts}
                                onChange={() => {
                                    formik.setFieldValue(
                                        'receipts.individualSalesReceipts',
                                        !formik.values.receipts.individualSalesReceipts,
                                    );
                                }}
                            />
                        </Box>
                    }
                />
            </Box>
        </>
    );
};

export default DineroBox;
